/**
 * The answer one recovery step gives: it found the paragraphs, it found more
 * than one it cannot choose between, or it found nothing. A union keyed on
 * `outcome`, built through the companion object's constructors so no step can
 * hand back a half-filled result.
 */
import { ON_PARAGRAPH, type RedlinePlacement } from "@common/utils/redline/wire/redline-placement";

import type { WordParagraph } from "../word-document";

/**
 * One place the server's locator could not choose between: the paragraph by
 * its index in the document, the words the server would anchor on there, and
 * why it ranked the place.
 *
 * NULL
 *   `anchorPhrase` is null when the server named the paragraph without quoting it.
 */
export interface AmbiguousCandidate {
    paragraphIndex: number;
    anchorPhrase: string | null;
    reason: string;
}

/**
 * What each of a card's five anchors said when the card-anchor step checked them
 * against the body, one short answer each, for the card's Details view.
 *
 * EXAMPLE
 *   id            "agrees"                                            the card's paraId is on one paragraph
 *   indexAndText  "differs: the paragraph at card index 12 reads differently"
 *   headingPath   "not stored"                                        the card carries no heading path
 *   neighbours    "agrees"
 *   position      "3% from where the card sat"
 */
export interface AnchorChecks {
    id: string;
    indexAndText: string;
    headingPath: string;
    neighbours: string;
    position: string;
}

/** The checks a result carries: the card-anchor step's five answers, or null from every other step. */
interface ResultChecks {
    checks: AnchorChecks | null;
}

export type RecoveryResult =
    | ({ outcome: "recovered"; paragraphs: WordParagraph[]; placement: RedlinePlacement; explanation: string | null } & ResultChecks)
    | ({ outcome: "ambiguous"; explanation: string; candidates: AmbiguousCandidate[] } & ResultChecks)
    | ({ outcome: "not_found"; explanation: string | null } & ResultChecks);

export type RecoveryOutcomeKind = RecoveryResult["outcome"];

export const RecoveryResult = {
    /** Paragraphs found on the paragraph itself, with no explanation beyond the step that found them. */
    recoveredOnParagraph(paragraphs: WordParagraph[]): RecoveryResult {
        return { outcome: "recovered", paragraphs, placement: ON_PARAGRAPH, explanation: null, checks: null };
    },

    /** Paragraphs found with a placement and a reason: the server's, or the card-anchor step's with its checks. */
    recovered({ paragraphs, placement, explanation, checks }: { paragraphs: WordParagraph[]; placement: RedlinePlacement; explanation: string | null; checks: AnchorChecks | null }): RecoveryResult {
        return { outcome: "recovered", paragraphs, placement, explanation, checks };
    },

    /** More than one place fits: the places, when the step can name them (the server's answer can; a session-only tie cannot). */
    ambiguous(explanation: string, candidates: AmbiguousCandidate[]): RecoveryResult {
        return { outcome: "ambiguous", explanation, candidates, checks: null };
    },

    /** More than one place fits after the card's anchors were checked: the places and what each check said. */
    ambiguousAfterChecks(explanation: string, candidates: AmbiguousCandidate[], checks: AnchorChecks): RecoveryResult {
        return { outcome: "ambiguous", explanation, candidates, checks };
    },

    notFound(explanation: string | null): RecoveryResult {
        return { outcome: "not_found", explanation, checks: null };
    },

    /** Nothing fits after the card's anchors were checked: what each check said. */
    notFoundAfterChecks(explanation: string, checks: AnchorChecks): RecoveryResult {
        return { outcome: "not_found", explanation, checks };
    },
};
